import { ReactNode } from 'react';

import { Tooltip } from '@flipster/react/design-system/tooltip';
import { css, cx } from '@flipster/styled-system/css';
import { grid, stack, vstack } from '@flipster/styled-system/patterns';

export interface StatDefinitionItem {
  label: ReactNode;
  value: ReactNode;
  /** value 아래에 덧붙는 보조 텍스트 (날짜, 카운트다운 등) */
  sub?: ReactNode;
  /** 있으면 label을 Tooltip trigger로 감싼다. 없으면 label을 그대로 렌더한다. */
  description?: ReactNode;
}

interface StatDefinitionGridProps {
  items: StatDefinitionItem[];
  /** <dl>에 덧붙일 클래스 (예: 하단 마진) */
  className?: string;
  /** <dt>에 덧붙일 클래스 (예: 라벨 색상) */
  dtClassName?: string;
  /** value와 sub 사이 세로 간격 */
  valueGap?: number;
}

/**
 * label/value(+선택적 sub, tooltip description) 항목을 반응형 정의 그리드로 렌더한다.
 * Trading parameter 패널과 TokenStats가 공유하는 dt/dd 그리드 구조.
 */
export const StatDefinitionGrid = ({ items, className, dtClassName, valueGap }: StatDefinitionGridProps) => (
  <dl className={cx(grid({ columns: { base: 1, smallDesktop: 4 }, gap: 12, textStyle: 'Text/M/Regular' }), className)}>
    {items.map((item, index) => (
      <div
        key={index}
        className={stack({
          direction: { base: 'row', smallDesktop: 'column' },
          justify: 'space-between',
          align: 'flex-start',
          alignSelf: 'flex-start',
        })}
      >
        <dt className={dtClassName}>
          {item.description ? (
            <Tooltip.Root placement='bottom-start' maxWidth={280}>
              <Tooltip.TextTrigger label={item.label} color='neutral-2' />
              <Tooltip.Content bottomSheetTitle={item.label}>{item.description}</Tooltip.Content>
            </Tooltip.Root>
          ) : (
            item.label
          )}
        </dt>
        <dd className={vstack({ gap: valueGap, alignItems: { base: 'flex-end', smallDesktop: 'flex-start' } })}>
          {item.value}
          {item.sub && (
            <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{item.sub}</span>
          )}
        </dd>
      </div>
    ))}
  </dl>
);
